// libs
import React from 'react'

// constants
import { CLASS_PREFIX } from 'src/constants'

// styles
import Icon from 'src/components/forms/icon/icon'
import { COLOR_TYPE_GRAY_TINT_14, COLOR_TYPE_PRIMARY } from 'src/utils/color'

import {
  ProjectPreviewFragmentFragment,
  useFollowMutation,
  useUnfollowMutation
} from '@lopo-tech/lopo-api'
import { useHistory } from 'react-router-dom'
import { LastActivityIcon } from 'src/components/icons/last-activity'
import NavigationItem from 'src/components/navigation_item/navigation_item'

import { textResources } from 'src/lang/de'
import DateFormatter from 'src/utils/date_formatter/date_formatter'
import { UrlUtils } from 'src/utils/url/url'
import './project_tile.scss'

const cls = `${CLASS_PREFIX}project-tile`

const t = textResources.projects

type IProps = {
  project: ProjectPreviewFragmentFragment;
};

const ProjectTile: React.FC<IProps> = ({ project }) => {
  const history = useHistory()
  const [follow] = useFollowMutation()
  const [unfollow] = useUnfollowMutation()

  const onClickFollow = async () => {
    if (project.isFollowing) {
      await unfollow({
        variables: {
          input: {
            followeeId: project.id
          }
        }
      })
    } else {
      await follow({
        variables: {
          input: {
            followeeId: project.id
          }
        }
      })
    }
  }

  const image = project.images[0]?.file.previewUrl
  const detailsUrl = UrlUtils.projectDetailPage(project.slug, project.type)

  return (
    <article className={`${cls}__card`}>
      <NavigationItem className={`${cls}__thumbnail`} onClick={() => history.push(detailsUrl)}>
        <img src={image} alt="" loading="lazy" />
      </NavigationItem>

      <div className={`${cls}__content`}>
        <div className={`${cls}__meta`}>
          <span className={`${cls}__meta-item`}>
            <LastActivityIcon
              sizeModifier={0.75}
              className={`${cls}__meta-last-activity`}
            />
            <span>
              {DateFormatter.asDayMonthYearShort(project.lastActivityAt)}
            </span>
          </span>

          {project.followersCount > 0 && (
            <>
              <span className={`${cls}__meta-dot`} aria-hidden />
              <span className={`${cls}__meta-item`}>
                <Icon
                  name={'participants'}
                  size={16}
                  color={COLOR_TYPE_GRAY_TINT_14}
                />
                <span>{project.followersCount}</span>
              </span>
            </>
          )}
        </div>

        <NavigationItem className={`${cls}__card-title`} onClick={() => history.push(detailsUrl)}>
          {project.name}
        </NavigationItem>

        {project.canShowTags.value && !!project.tags?.length && (
          <div className={`${cls}__tags`}>
            {project.tags.map((tag) => (
              <span className={`${cls}__tag`} key={tag.id}>
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {project.canFollow.value && (
        <div className={`${cls}__action`}>
          <button
            type="button"
            aria-label={t.common.followProject}
            onClick={onClickFollow}
          >
            <Icon
              name={project.isFollowing ? 'check' : 'plus'}
              size={project.isFollowing ? 16 : 26}
              color={COLOR_TYPE_PRIMARY}
            />
          </button>
        </div>
      )}
    </article>
  )
}

export default ProjectTile
